import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Users2, Send, Loader2, X, UserPlus } from "lucide-react";

const inviteSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().optional(),
  email: z.string().email("Invalid email address"),
  phone: z.string().optional(),
  personalMessage: z.string().max(500, "Message must be under 500 characters").optional(),
  joinTeam: z.boolean().default(false),
});

type InviteFormData = z.infer<typeof inviteSchema>;

interface SearchResult {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
}

interface StartTeamInviteFlowProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  challengeId: string;
  challengeTitle: string;
  onSuccess?: () => void;
}

const StartTeamInviteFlow: React.FC<StartTeamInviteFlowProps> = ({
  open,
  onOpenChange,
  challengeId,
  challengeTitle,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedUser, setSelectedUser] = useState<SearchResult | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const form = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      personalMessage: "",
      joinTeam: false,
    },
  });

  // Debounced search for users
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchTerm.length < 2 || selectedUser) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const { data, error } = await supabase.rpc("search_users_for_invite", {
          search_term: searchTerm,
        });

        if (error) throw error;
        // Map the RPC response to our SearchResult interface
        const results: SearchResult[] = (data || []).map((u: any) => ({
          id: u.user_id,
          email: u.email,
          first_name: null,
          last_name: null,
          display_name: u.display_name,
        }));
        setSearchResults(results);
        setShowDropdown(true);
      } catch (error) {
        console.error("Error searching users:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, selectedUser]);

  const handleSelectUser = useCallback(
    (user: SearchResult) => {
      setSelectedUser(user);
      setSearchResults([]);
      setShowDropdown(false);
      setSearchTerm("");
      form.setValue("firstName", user.first_name || user.display_name?.split(" ")[0] || "");
      form.setValue("lastName", user.last_name || user.display_name?.split(" ").slice(1).join(" ") || "");
      form.setValue("email", user.email);
    },
    [form]
  );

  const handleClearSelection = useCallback(() => {
    setSelectedUser(null);
    form.reset({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      personalMessage: form.getValues("personalMessage"),
      joinTeam: form.getValues("joinTeam"),
    });
  }, [form]);

  const handleSearchInputChange = (value: string) => {
    setSearchTerm(value);
    if (!selectedUser) {
      form.setValue("firstName", value);
    }
  };

  const handleAddNewPerson = () => {
    setShowDropdown(false);
    form.setValue("firstName", searchTerm);
  };

  const onSubmit = async (data: InviteFormData) => {
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in to send invites");
        return;
      }

      // Create invitation with timeout protection
      const insertPromise = supabase
        .from("team_member_invitations")
        .insert({
          team_id: null, // No team exists yet
          challenge_id: challengeId,
          invited_by: user.id,
          invitee_name: `${data.firstName} ${data.lastName || ""}`.trim(),
          invitee_first_name: data.firstName,
          invitee_last_name: data.lastName || null,
          invitee_email: data.email,
          invitee_phone: data.phone || null,
          invitation_type: "create_team",
          original_inviter_id: user.id,
          inviter_joins_team: data.joinTeam,
          personal_message: data.personalMessage || null,
        })
        .select("id")
        .single();

      // Race against 15-second timeout to prevent infinite loading
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error("Request timed out. Please try again.")), 15000)
      );

      const { data: invitation, error: inviteError } = await Promise.race([
        insertPromise,
        timeoutPromise
      ]);

      if (inviteError) throw inviteError;

      // Send the invite email
      const { error: emailError } = await supabase.functions.invoke("send-team-invite", {
        body: { invitation_id: invitation.id },
      });

      if (emailError) {
        console.error("Email error:", emailError);
        toast.success("Invitation created! (Email may be delayed)");
      } else {
        toast.success(`Invitation sent to ${data.email}`);
      }

      form.reset();
      setSelectedUser(null);
      setSearchTerm("");
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      console.error("Error sending invite:", error);
      toast.error(error.message || "Failed to send invitation");
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      form.reset();
      setSelectedUser(null);
      setSearchTerm("");
      setSearchResults([]);
      setShowDropdown(false);
    }
  }, [open, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Users2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle>Invite to Start a New Team</DialogTitle>
            </div>
          </div>
          <DialogDescription>
            Invite someone to create and lead a new team for <strong>"{challengeTitle}"</strong>. 
            They'll create the team when they accept.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Selected User Badge */}
            {selectedUser && (
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="flex items-center gap-2 py-1.5 px-3">
                  <span>{selectedUser.display_name || selectedUser.email}</span>
                  <button
                    type="button"
                    onClick={handleClearSelection}
                    className="hover:bg-muted rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              </div>
            )}

            {/* First Name with Search */}
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="relative">
                  <FormLabel>First Name *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Search or enter name..."
                      {...field}
                      value={selectedUser ? field.value : searchTerm || field.value}
                      onChange={(e) => {
                        if (!selectedUser) {
                          handleSearchInputChange(e.target.value);
                        }
                        field.onChange(e);
                      }}
                      onFocus={() => {
                        if (searchResults.length > 0) setShowDropdown(true);
                      }}
                      disabled={!!selectedUser}
                    />
                  </FormControl>
                  {showDropdown && !selectedUser && (searchResults.length > 0 || isSearching || searchTerm.length >= 2) && (
                    <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg">
                      <Command>
                        <CommandList>
                          {isSearching && (
                            <div className="p-2 text-sm text-muted-foreground flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Searching...
                            </div>
                          )}
                          {!isSearching && searchResults.length === 0 && searchTerm.length >= 2 && (
                            <CommandEmpty>
                              <button
                                type="button"
                                onClick={handleAddNewPerson}
                                className="w-full p-2 text-left hover:bg-accent flex items-center gap-2"
                              >
                                <UserPlus className="h-4 w-4" />
                                Add "{searchTerm}" as new person
                              </button>
                            </CommandEmpty>
                          )}
                          {searchResults.length > 0 && (
                            <CommandGroup heading="Existing Users">
                              {searchResults.map((user) => (
                                <CommandItem
                                  key={user.id}
                                  value={user.email}
                                  onSelect={() => handleSelectUser(user)}
                                  className="cursor-pointer"
                                >
                                  <div className="flex flex-col">
                                    <span className="font-medium">
                                      {user.display_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email}
                                    </span>
                                    <span className="text-xs text-muted-foreground">{user.email}</span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}
                        </CommandList>
                      </Command>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Last Name */}
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Doe" {...field} disabled={!!selectedUser} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input 
                      type="email" 
                      placeholder="john@example.com" 
                      {...field} 
                      disabled={!!selectedUser} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Phone */}
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input 
                      type="tel" 
                      placeholder="(555) 123-4567" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Personal Message */}
            <FormField
              control={form.control}
              name="personalMessage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Personal Message (optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Add a personal note to your invitation..."
                      className="resize-none"
                      rows={3}
                      {...field} 
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground mt-1">
                    {field.value?.length || 0}/500 characters
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Join Team Checkbox */}
            <FormField
              control={form.control}
              name="joinTeam"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      Add me to the team when it's created
                    </FormLabel>
                    <p className="text-sm text-muted-foreground">
                      You'll automatically join as a member when they accept
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="gap-2">
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Send Invitation
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default StartTeamInviteFlow;
